import { type ReactNode } from 'react';

import { MediaStudioDialog, type MediaStudioMeta } from '../media-studio/MediaStudioDialog';
import { type SuggestionChip } from '../media-studio/SuggestionChips';

export type ImageStudioDialogMeta = MediaStudioMeta;

export interface ImageStudioDialogContext {
  assetId: string;
  imageUrl: string;
  prompt?: string;
  model?: string;
  aspectRatio?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  title: string;
  meta: MediaStudioMeta;
  thumbnailUrl?: string;
  savedToLibrary?: boolean;
  onVariations?: () => void;
  onDownload?: () => void;
  onSaveToPlan?: () => void;
  onPushToShopify?: () => void;
  onChipAction?: (chip: SuggestionChip) => void | Promise<void>;
  inline?: boolean;
  breadcrumb?: { rootLabel: string; rootHref?: string | undefined; leafLabel: string } | undefined;
  context?: ImageStudioDialogContext;
  onCustomPrompt?: (prompt: string) => Promise<void> | void;
  onCropSave?: (blob: Blob) => Promise<void> | void;
  onCreatePost?: () => void;
  onCreateWithMora?: () => void;
  isCreatingPost?: boolean;
  isOperating?: boolean;
  activeOperationId?: string | null;
  regenerable?: boolean;
  artifactId?: string | null;
  artifactKind?: string;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onRotate90?: () => void;
  onFlipHorizontal?: () => void;
  onFlipVertical?: () => void;
}

export function ImageStudioDialog(props: Props): ReactNode {
  const ctx = props.context;
  if (!ctx) return null;

  return (
    <MediaStudioDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      inline={props.inline}
      {...(props.breadcrumb ? { breadcrumb: props.breadcrumb } : {})}
      savedToLibrary={props.savedToLibrary}
      onDownload={props.onDownload}
      onSaveToPlan={props.onSaveToPlan}
      onVariations={props.onVariations}
      onPushToShopify={props.onPushToShopify}
      onChipAction={props.onChipAction}
      onCustomPrompt={props.onCustomPrompt}
      onCreatePost={props.onCreatePost}
      onCreateWithMora={props.onCreateWithMora}
      isCreatingPost={props.isCreatingPost}
      onCropSave={props.onCropSave}
      isOperating={props.isOperating}
      activeOperationId={props.activeOperationId}
      regenerable={props.regenerable}
      artifactId={props.artifactId}
      artifactKind={props.artifactKind}
      onUndo={props.onUndo}
      onRedo={props.onRedo}
      canUndo={props.canUndo}
      canRedo={props.canRedo}
      onRotate90={props.onRotate90}
      onFlipHorizontal={props.onFlipHorizontal}
      onFlipVertical={props.onFlipVertical}
      payload={{
        kind: 'image',
        url: props.imageUrl,
        title: props.title,
        meta: props.meta,
        context: { kind: 'image', ...ctx },
        thumbnailUrl: props.thumbnailUrl,
      }}
    />
  );
}
